import {
  DEFAULT_VISIBLE_ICON_TYPES,
  DEFAULT_VISIBLE_PHASE_BANDS,
  ICON_TYPE_OPTIONS,
  MAX_VISIBLE_PHASE_BANDS,
  PHASE_BAND_OPTIONS,
} from "@nfp-tracker/components";
import type { CyclePhaseKey } from "@nfp-tracker/utils";
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "nfp.calendar.viewPreferences";

const VALID_ICON_KEYS = new Set(ICON_TYPE_OPTIONS.map((option) => option.key));
const VALID_PHASE_KEYS = new Set(PHASE_BAND_OPTIONS.map((option) => option.key));

type StoredPreferences = {
  visibleIconTypes: string[];
  visiblePhases: CyclePhaseKey[];
  /**
   * Every icon key this build knew about when the preferences were saved. Lets a later build
   * tell "the user hid this icon" apart from "this icon didn't exist yet" — a newly added
   * default-visible icon type is switched on for existing users instead of staying hidden.
   */
  knownIconKeys: string[];
};

/** Icon types introduced after `knownIconKeys` started being stored (0.5.0). */
const ICON_KEYS_ADDED_SINCE_LEGACY_PREFS = new Set<string>(["ovulation_test"]);

const defaults = (): StoredPreferences => ({
  visibleIconTypes: [...DEFAULT_VISIBLE_ICON_TYPES],
  visiblePhases: [...DEFAULT_VISIBLE_PHASE_BANDS],
  knownIconKeys: [...VALID_ICON_KEYS],
});

/**
 * Stored icon toggles, with any default-visible icon type the saving build didn't know about
 * unioned in. Exported for tests.
 */
export const reconcileVisibleIconTypes = (
  storedVisible: string[],
  storedKnown: string[] | undefined
): string[] => {
  const visible = storedVisible.filter((key) => VALID_ICON_KEYS.has(key));
  // Preferences saved before `knownIconKeys` existed predate exactly the keys in
  // ICON_KEYS_ADDED_SINCE_LEGACY_PREFS; every other key was already toggleable then, so a
  // deliberate "hidden" must be respected rather than reset.
  const known = new Set(
    Array.isArray(storedKnown)
      ? storedKnown
      : [...VALID_ICON_KEYS].filter((key) => !ICON_KEYS_ADDED_SINCE_LEGACY_PREFS.has(key))
  );
  for (const key of DEFAULT_VISIBLE_ICON_TYPES) {
    if (!known.has(key) && !visible.includes(key)) visible.push(key);
  }
  return visible;
};

/**
 * Unknown keys are dropped rather than trusted — a stored preference from an older build
 * could name an icon or phase this build no longer has.
 */
const readStored = (): StoredPreferences => {
  if (typeof window === "undefined") return defaults();

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults();

    const parsed = JSON.parse(raw) as Partial<StoredPreferences>;
    const fallback = defaults();

    return {
      visibleIconTypes: Array.isArray(parsed.visibleIconTypes)
        ? reconcileVisibleIconTypes(parsed.visibleIconTypes, parsed.knownIconKeys)
        : fallback.visibleIconTypes,
      knownIconKeys: fallback.knownIconKeys,
      visiblePhases: Array.isArray(parsed.visiblePhases)
        ? parsed.visiblePhases
            .filter((key) => VALID_PHASE_KEYS.has(key))
            .slice(0, MAX_VISIBLE_PHASE_BANDS)
        : fallback.visiblePhases,
    };
  } catch {
    return defaults();
  }
};

/**
 * Icon and phase-band toggles for the calendar/cycle grids, persisted locally so navigating
 * away and back doesn't silently reset a view the user just set up.
 */
export const useCalendarViewPreferences = () => {
  const [preferences, setPreferences] = useState<StoredPreferences>(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Private-mode or quota failures shouldn't break the view; the toggles still work
      // for this session, they just won't be remembered.
    }
  }, [preferences]);

  const toggleIconType = useCallback((key: string) => {
    setPreferences((current) => ({
      ...current,
      visibleIconTypes: current.visibleIconTypes.includes(key)
        ? current.visibleIconTypes.filter((k) => k !== key)
        : [...current.visibleIconTypes, key],
    }));
  }, []);

  const togglePhase = useCallback((key: CyclePhaseKey) => {
    setPreferences((current) => {
      if (current.visiblePhases.includes(key)) {
        return {
          ...current,
          visiblePhases: current.visiblePhases.filter((k) => k !== key),
        };
      }
      // Cap enforced here as well as in the legend UI, so a stale stored value or a second
      // caller can't push the stack past what stays readable above a day square.
      if (current.visiblePhases.length >= MAX_VISIBLE_PHASE_BANDS) return current;
      return { ...current, visiblePhases: [...current.visiblePhases, key] };
    });
  }, []);

  return {
    visibleIconTypes: preferences.visibleIconTypes,
    visiblePhases: preferences.visiblePhases,
    toggleIconType,
    togglePhase,
  };
};
